import React, { useState } from "react";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowRight, Copy, Check } from "lucide-react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import SEOHead from "@/components/SEOHead";

const ease = [0.16, 1, 0.3, 1];
const fadeUp = (delay = 0) => ({
  initial: { opacity: 0, y: 20 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-50px" },
  transition: { duration: 0.6, ease, delay },
});

const CopySnippet = ({ text }) => {
  const [copied, setCopied] = useState(false);
  return (
    <div className="relative bg-ink rounded-2xl p-5 my-4">
      <pre className="text-green-300/80 text-xs font-mono leading-relaxed whitespace-pre-wrap">{text}</pre>
      <button onClick={() => { navigator.clipboard.writeText(text); setCopied(true); setTimeout(() => setCopied(false), 2000); }}
        className={`absolute top-3 right-3 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium transition-all ${copied ? "bg-green-500 text-white" : "bg-white/10 text-white/70 hover:bg-brand hover:text-white"}`}>
        {copied ? <><Check size={11} /> Copied</> : <><Copy size={11} /> Copy</>}
      </button>
    </div>
  );
};

const SECTIONS = [
  {
    num: "01",
    title: "The Headline Formula That Gets Noticed",
    body: `Your LinkedIn headline is the most valuable real estate on your profile — it appears in search results, connection requests, and recruiter inboxes.

Most engineers write: "Software Engineer at [Company]"
That tells recruiters nothing about what you're good at or what you want next.

Use this formula instead:`,
    snippet: `[What you do] | [Specialization] | [What you're targeting or open to]

Examples:
• Backend Engineer | Distributed Systems & APIs | Open to Senior SDE Roles
• ML Engineer | NLP & LLM Systems | Targeting AI Infra Roles at Product Companies
• Full-Stack SDE | React + Node.js | Actively Seeking 2025 Opportunities`,
  },
  {
    num: "02",
    title: "The About Section — Tell a Story, Not a List",
    body: `The About section is the one place on LinkedIn where you can write in first person and show your personality. Most engineers either leave it blank or paste their resume summary.

Structure yours like this (3 short paragraphs):

Paragraph 1 — Who you are and what you're known for
Paragraph 2 — 1–2 specific achievements or projects (with numbers)
Paragraph 3 — What you're looking for and how to reach you

Keep it under 220 words. Use simple language. No buzzwords.`,
    snippet: `Good About section example:

"I'm a backend engineer with 5 years of experience building APIs and data pipelines at scale. I specialize in Python and distributed systems — the kind of infrastructure that has to work at 3am.

At [Company], I led a team that rebuilt our job queue system from scratch, reducing processing failures by 80% and cutting infrastructure costs by $120K annually. Before that, I built the data ingestion layer that processes 4TB of logs daily.

I'm currently exploring senior backend and platform engineering roles at product-led companies. Feel free to reach out: yourname@email.com"`,
  },
  {
    num: "03",
    title: "Your Featured Section = Your Second Portfolio",
    body: `The Featured section sits above your experience and is the first thing people scroll to. Most engineers ignore it completely.

Use it to pin:
- A link to your strongest GitHub project (with a clear description)
- A technical blog post or article you've written
- A conference talk or presentation
- Your portfolio website

Even one strong Featured item signals that you do more than just show up at work.`,
    snippet: null,
  },
  {
    num: "04",
    title: "Experience Bullets — Same Rules as Your Resume",
    body: `Your LinkedIn experience section should mirror your resume — CAR-format bullets with action verbs and metrics. Recruiters often look at LinkedIn before the resume.

One extra rule for LinkedIn: you can add 3–5 bullets per role, which is slightly more than a one-page resume allows. Use that space to include one or two extra achievements.`,
    snippet: `Weak: "Worked on the recommendation system team"

Strong: "Redesigned cold-start logic for recommendation engine, improving new user engagement 
by 22% in A/B test affecting 1.2M weekly active users"`,
  },
  {
    num: "05",
    title: "Skills — Order Matters More Than Count",
    body: `LinkedIn allows up to 50 skills. The first 3 are shown by default (without clicking "Show more") — so put your most recruiter-searched skills first.

For engineers in 2025, prioritize:
- Your core language (Python, Java, Go, etc.)
- Your strongest system/domain area
- Your most in-demand framework

Then fill in the rest. Don't add soft skills ("Communication", "Leadership") — they dilute your technical signal.`,
    snippet: `Priority skills for common roles:
• ML Engineer:   Python, PyTorch, Machine Learning, MLOps, AWS, SQL
• Backend SDE:   Java / Python / Go, System Design, Distributed Systems, Kubernetes
• Full-Stack:    React, Node.js, TypeScript, REST APIs, PostgreSQL
• Data Engineer: Apache Spark, Airflow, SQL, Python, BigQuery, dbt`,
  },
  {
    num: "06",
    title: "Activity: Show You're Engaged",
    body: `LinkedIn rewards active users with more profile visibility. You don't need to post every day — just enough to stay visible in your network's feed.

What works for engineers:
- Share a short technical lesson once or twice a month
- Comment thoughtfully on 2–3 posts per week from people in your target companies
- Repost job search updates from your network with a short note

Even 20 minutes a week of consistent activity can meaningfully increase profile views.`,
    snippet: null,
  },
  {
    num: "07",
    title: "The Prompt to Optimize Your Full LinkedIn Profile",
    body: `Use this prompt to get a complete LinkedIn profile review and rewrite:`,
    snippet: `I'm a [Job Title] with [X] years of experience targeting [target role] at [target companies].

Please review and rewrite the following sections of my LinkedIn profile:

1. HEADLINE — use the formula: [What I do] | [Specialization] | [What I'm targeting]
2. ABOUT — 3 short paragraphs: who I am, 1-2 achievements with numbers, what I'm looking for
3. EXPERIENCE bullets — CAR format, strong action verbs, at least 1 metric per bullet
4. SKILLS — reorder my top 3 to be most recruiter-searched for my target role

My current profile content:
[Paste your current headline, about section, and experience bullets here]

Target role JD (optional):
[Paste JD here for keyword alignment]`,
  },
];

export default function BlogPostLinkedInGuidePage() {
  return (
    <div data-testid="blog-linkedin-page" className="bg-white">
      <SEOHead
        title="LinkedIn Optimization Guide for Software Engineers | ApplyStrat"
        description="How to optimize your LinkedIn profile as a software engineer or ML engineer — headline, about section, featured, skills, and activity strategy."
        canonical="https://applystrat.com/blog/linkedin-optimization-guide"
      />

      <section className="pt-36 pb-12 text-center" style={{ background: "radial-gradient(circle at 50% -20%, rgba(255,82,0,0.10) 0%, transparent 60%)" }}>
        <div className="max-w-3xl mx-auto px-6">
          <motion.div {...fadeUp()}>
            <Link to="/blog" className="inline-flex items-center gap-1.5 text-ink-secondary text-sm hover:text-ink transition-colors mb-8">
              <ArrowLeft size={14} /> Back to Blog
            </Link>
            <div className="flex items-center justify-center gap-3 mb-6">
              <span className="px-3 py-1 rounded-full bg-brand-light text-brand text-xs font-medium">LinkedIn</span>
              <span className="text-ink-secondary text-xs">6 min read</span>
            </div>
            <h1 className="text-4xl md:text-5xl font-medium tracking-tighter text-ink mb-5 leading-tight">
              The LinkedIn Optimization<br /><span className="text-brand">Guide for Engineers</span>
            </h1>
            <p className="text-lg text-ink-secondary max-w-xl mx-auto leading-relaxed">
              Most engineers treat LinkedIn as a resume backup. Here's how to turn it into an inbound recruiter machine.
            </p>
          </motion.div>
        </div>
      </section>

      <section className="py-12 bg-white">
        <div className="max-w-2xl mx-auto px-6">
          <motion.p {...fadeUp()} className="text-ink-secondary leading-relaxed text-base">
            LinkedIn is how recruiters at Google, Amazon, and Microsoft find candidates before they ever open a job portal. A well-optimized profile can generate inbound recruiter messages every week — without you doing anything. These 7 sections will get your profile there.
          </motion.p>
        </div>
      </section>

      <section className="pb-24 bg-surface-raised">
        <div className="max-w-2xl mx-auto px-6 pt-4">
          <div className="space-y-8">
            {SECTIONS.map((s, i) => (
              <motion.div key={s.num} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.5, ease, delay: 0.05 }}
                data-testid={`linkedin-section-${i + 1}`}
                className="bg-white rounded-3xl p-8 shadow-sm border border-black/5">
                <div className="flex items-center gap-3 mb-4">
                  <span className="text-3xl font-medium text-brand/20">{s.num}</span>
                </div>
                <h2 className="text-ink font-medium text-lg mb-4">{s.title}</h2>
                <p className="text-ink-secondary text-sm leading-relaxed whitespace-pre-line">{s.body}</p>
                {s.snippet && <CopySnippet text={s.snippet} />}
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 bg-white">
        <div className="max-w-3xl mx-auto px-6 text-center">
          <motion.div {...fadeUp()}>
            <h2 className="text-3xl font-medium text-ink mb-4">Put It All Together</h2>
            <p className="text-ink-secondary text-base mb-8">Resume + LinkedIn + application strategy. If you want all three handled for you, that's what ApplyStrat does.</p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <Button asChild size="lg" className="shadow-sm">
                <Link to="/resume-optimizer">Optimize My Resume Too <ArrowRight size={16} /></Link>
              </Button>
              <Button asChild variant="secondary">
                <Link to="/apply">Work With ApplyStrat</Link>
              </Button>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
